import { useMemo } from "react";

import type { AppData, ConnectorData } from "~/slices/appDesignSlice";
import styles from "./AppDiagram.module.scss";
import type { DiagramVariant } from "./appDiagramLayout";
import {
  assignLayers,
  computeEdge,
  computeEdgeLabelPosition,
  computeNodePositions,
  filterTopApps,
  getLayoutConfig,
  groupEdgesByPair,
  mergeLabelGroups,
} from "./appDiagramLayout";

const KINTONE_ICON_URL = "/assets/KintoneIcon.png";
const DISPLAY_LIMIT = 10;

const EDGE_COLORS: Record<string, string> = {
  ルックアップ: "#3B82F6",
  関連レコード: "#F97316",
  関連レコード一覧: "#F97316",
};
const DEFAULT_EDGE_COLOR = "#94a3b8";

const EDGE_OFFSET_PX = 16;

type EdgeData = {
  connectorid: number;
  line: { x1: number; y1: number; x2: number; y2: number };
  labelPos: { x: number; y: number };
  relationship: string;
  color: string;
};

type AppDiagramProps = {
  apps: AppData[];
  connectors: ConnectorData[];
  variant?: DiagramVariant;
};

export const AppDiagram = ({ apps, connectors, variant = "portal" }: AppDiagramProps) => {
  const layoutConfig = getLayoutConfig(variant);

  const {
    filteredApps,
    overflowCount,
    positions,
    edges,
    edgeColors,
    labelGroups,
    diagramHeight,
    diagramWidth,
  } = useMemo(() => {
    if (apps.length === 0) {
      return {
        filteredApps: [] as AppData[],
        overflowCount: 0,
        positions: new Map<number, { x: number; y: number }>(),
        edges: [] as EdgeData[],
        edgeColors: [] as string[],
        labelGroups: [] as {
          key: string;
          pos: { x: number; y: number };
          labels: { relationship: string; color: string }[];
        }[],
        diagramHeight: 0,
        diagramWidth: 0,
      };
    }

    const {
      apps: filteredApps,
      connectors: filteredConnectors,
      overflowCount,
    } = filterTopApps(apps, connectors, DISPLAY_LIMIT);

    const layers = assignLayers(filteredApps, filteredConnectors);
    const positions = computeNodePositions(layers, layoutConfig, filteredConnectors);

    const grouped = groupEdgesByPair(filteredConnectors);

    // Per-node slot assignment, split by edge direction:
    //   - Cross-layer peers → offset on X axis (vertical edges)
    //   - Same-layer peers → offset on Y axis (horizontal edges)
    const SLOT_SPACING = EDGE_OFFSET_PX;
    const PAIR_SPACING = EDGE_OFFSET_PX * 0.6;

    const nodeSlots = new Map<number, Map<number, { slotOffset: number }>>();
    for (const nodeId of Array.from(positions.keys())) {
      const nodeY = positions.get(nodeId)!.y;
      const crossLayerPeers: number[] = [];
      const sameLayerPeers: number[] = [];

      const peerIds = new Set<number>();
      for (const c of filteredConnectors) {
        if (c.sid === nodeId && positions.has(c.tid)) peerIds.add(c.tid);
        if (c.tid === nodeId && positions.has(c.sid)) peerIds.add(c.sid);
      }
      for (const peerId of Array.from(peerIds)) {
        if (positions.get(peerId)!.y === nodeY) {
          sameLayerPeers.push(peerId);
        } else {
          crossLayerPeers.push(peerId);
        }
      }

      crossLayerPeers.sort((a, b) => (positions.get(a)?.x ?? 0) - (positions.get(b)?.x ?? 0));
      sameLayerPeers.sort((a, b) => (positions.get(a)?.x ?? 0) - (positions.get(b)?.x ?? 0));

      const slotMap = new Map<number, { slotOffset: number }>();
      crossLayerPeers.forEach((peerId, i) => {
        slotMap.set(peerId, {
          slotOffset: (i - (crossLayerPeers.length - 1) / 2) * SLOT_SPACING,
        });
      });
      sameLayerPeers.forEach((peerId, i) => {
        slotMap.set(peerId, {
          slotOffset: (i - (sameLayerPeers.length - 1) / 2) * SLOT_SPACING,
        });
      });
      nodeSlots.set(nodeId, slotMap);
    }

    const edges: EdgeData[] = grouped
      .filter((g) => positions.has(g.connector.sid) && positions.has(g.connector.tid))
      .map((g) => {
        const { connector, groupIndex, groupCount } = g;
        const pairOffset = (groupIndex - (groupCount - 1) / 2) * PAIR_SPACING;
        const sameLayer = positions.get(connector.sid)!.y === positions.get(connector.tid)!.y;

        // Horizontal edges: pair offset only (left/right sides already separate peers)
        // Vertical edges: slot offset + pair offset
        const srcSlot = sameLayer
          ? 0
          : (nodeSlots.get(connector.sid)?.get(connector.tid)?.slotOffset ?? 0);
        const tgtSlot = sameLayer
          ? 0
          : (nodeSlots.get(connector.tid)?.get(connector.sid)?.slotOffset ?? 0);

        const line = computeEdge(
          positions.get(connector.sid)!,
          positions.get(connector.tid)!,
          layoutConfig.cardWidth,
          layoutConfig.cardHeight,
          srcSlot + pairOffset,
          tgtSlot + pairOffset,
        );
        const labelPos = computeEdgeLabelPosition(line);
        const color = EDGE_COLORS[connector.relationship] ?? DEFAULT_EDGE_COLOR;
        return {
          connectorid: connector.connectorid,
          line,
          labelPos,
          relationship: connector.relationship,
          color,
        };
      });

    const edgeColors = Array.from(new Set(edges.map((e) => e.color)));

    // Group labels by pair, then merge nearby groups to avoid overlapping labels
    const pairLabelMap = new Map<
      string,
      { pos: { x: number; y: number }; labels: { relationship: string; color: string }[] }
    >();
    for (const g of grouped) {
      const c = g.connector;
      if (!positions.has(c.sid) || !positions.has(c.tid)) continue;
      const key = `${Math.min(c.sid, c.tid)}-${Math.max(c.sid, c.tid)}`;
      if (!pairLabelMap.has(key)) {
        const centerLine = computeEdge(
          positions.get(c.sid)!,
          positions.get(c.tid)!,
          layoutConfig.cardWidth,
          layoutConfig.cardHeight,
        );
        pairLabelMap.set(key, {
          pos: computeEdgeLabelPosition(centerLine),
          labels: [],
        });
      }
      const color = EDGE_COLORS[c.relationship] ?? DEFAULT_EDGE_COLOR;
      pairLabelMap.get(key)!.labels.push({ relationship: c.relationship, color });
    }
    const rawGroups = Array.from(pairLabelMap.entries()).map(([key, val]) => ({
      key,
      ...val,
    }));
    const labelGroups = mergeLabelGroups(rawGroups);

    const allPositions = Array.from(positions.values());
    const maxX = allPositions.length > 0 ? Math.max(...allPositions.map((p) => p.x)) : 0;
    const maxY = allPositions.length > 0 ? Math.max(...allPositions.map((p) => p.y)) : 0;

    return {
      filteredApps,
      overflowCount,
      positions,
      edges,
      edgeColors,
      labelGroups,
      diagramHeight: maxY + layoutConfig.cardHeight + 24,
      diagramWidth: maxX + layoutConfig.cardWidth + 24,
    };
  }, [apps, connectors, layoutConfig]);

  if (apps.length === 0) {
    return <div className={styles["empty"]}>アプリ設計データがありません</div>;
  }

  return (
    <div className={styles["container"]}>
      <div className={styles["diagramArea"]}>
        <div
          className={styles["diagramContent"]}
          style={{ height: diagramHeight, minWidth: diagramWidth }}
        >
          {filteredApps.map((app) => {
            const pos = positions.get(app.appid);
            if (!pos) return null;
            return (
              <div
                key={app.appid}
                className={variant === "compact" ? styles["appCardCompact"] : styles["appCard"]}
                style={{ left: pos.x, top: pos.y }}
              >
                {variant === "compact" ? (
                  <>
                    <div className={styles["appIconBadge"]}>
                      <img src={KINTONE_ICON_URL} alt="" className={styles["appIconSmall"]} />
                    </div>
                    <span className={styles["appNameCompact"]}>{app.appname}</span>
                  </>
                ) : (
                  <>
                    <div className={styles["appIconArea"]}>
                      <img src={KINTONE_ICON_URL} alt="" className={styles["appIcon"]} />
                    </div>
                    <span className={styles["appName"]}>{app.appname}</span>
                  </>
                )}
              </div>
            );
          })}
          {edges.length > 0 && (
            <svg
              aria-hidden="true"
              className={styles["svgOverlay"]}
              style={{ height: diagramHeight, minWidth: diagramWidth }}
            >
              <defs>
                {edgeColors.map((color) => (
                  <marker
                    key={color}
                    id={`arrow-${color.replace("#", "")}`}
                    viewBox="0 0 10 10"
                    refX="10"
                    refY="5"
                    markerWidth="8"
                    markerHeight="8"
                    orient="auto-start-reverse"
                  >
                    <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
                  </marker>
                ))}
              </defs>
              {edges.map((e) => (
                <line
                  key={e.connectorid}
                  x1={e.line.x1}
                  y1={e.line.y1}
                  x2={e.line.x2}
                  y2={e.line.y2}
                  stroke={e.color}
                  strokeWidth={1.5}
                  markerEnd={`url(#arrow-${e.color.replace("#", "")})`}
                />
              ))}
            </svg>
          )}
          {labelGroups.map((g) => (
            <div
              key={`label-group-${g.key}`}
              className={styles["edgeLabelGroup"]}
              style={{ left: g.pos.x, top: g.pos.y }}
            >
              {g.labels.map((l, i) => (
                <div
                  key={`${l.relationship}-${i}`}
                  className={styles["edgeLabel"]}
                  style={{ borderColor: l.color, color: l.color }}
                >
                  {l.relationship}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className={styles["legend"]}>
        <span className={styles["legendItem"]}>
          アプリ: {filteredApps.length}件{overflowCount > 0 && `（他 ${overflowCount} 件）`}
        </span>
      </div>
    </div>
  );
};
